// Copyright (c) 2026 Goffy. All Rights Reserved.

import { create } from 'zustand';

type DebugTraceState = {
  holeViewTraceId: string | null;
  holeScoreTraceId: string | null;
  setHoleViewTraceId: (traceId: string) => void;
  setHoleScoreTraceId: (traceId: string) => void;
};

const STORAGE_KEY = 'goffy.debug.traceIds';

type StoredTraceState = {
  holeViewTraceId?: string | null;
  holeScoreTraceId?: string | null;
};

const getStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

const readStoredState = (): StoredTraceState => {
  const storage = getStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredTraceState;
    return parsed ?? {};
  } catch {
    return {};
  }
};

const persistState = (next: StoredTraceState) => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore storage failures; keep in-memory state.
  }
};

const stored = readStoredState();

export const useDebugTraceState = create<DebugTraceState>((set, get) => ({
  holeViewTraceId: stored.holeViewTraceId ?? null,
  holeScoreTraceId: stored.holeScoreTraceId ?? null,
  setHoleViewTraceId: (traceId) => {
    const { holeScoreTraceId } = get();
    persistState({ holeViewTraceId: traceId, holeScoreTraceId });
    set({ holeViewTraceId: traceId });
  },
  setHoleScoreTraceId: (traceId) => {
    const { holeViewTraceId } = get();
    persistState({ holeViewTraceId, holeScoreTraceId: traceId });
    set({ holeScoreTraceId: traceId });
  },
}));
